import React from "react";

// MSAL imports
import { EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import ReactDOM from "react-dom/client";

import App from "./App";
import { msalConfig } from "./authConfig";
import { loginRequest } from "./authConfig";
import "./index.css";

export const msalInstance = new PublicClientApplication(msalConfig);

msalInstance.initialize().then(() => {
  // Paint IMMEDIATELY after initialize. The auth bootstrap below (redirect
  // handling + silent SSO) must NOT gate the first render: on a fresh/incognito
  // session ssoSilent opens a hidden iframe to Entra that hangs until MSAL's
  // ~10s iframe timeout, which showed as a ~10-15s white screen. MsalProvider and
  // the event callback keep account state consistent once the background auth
  // resolves, so nothing is lost by rendering first.
  const root = ReactDOM.createRoot(document.getElementById("root"));
  root.render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    </React.StrictMode>
  );

  // Register listeners before the async auth so no LOGIN/SSO events are missed.
  msalInstance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
      event.eventType === EventType.SSO_SILENT_SUCCESS
    ) {
      const account = event.payload.account;
      msalInstance.setActiveAccount(account);
    }
  });
  // Optional - updates account state if a user signs in from another tab/window.
  msalInstance.enableAccountStorageEvents();

  // Auth bootstrap runs in the BACKGROUND — never blocks the first paint.
  (async () => {
    try {
      const redirectResponse = await msalInstance.handleRedirectPromise();
      if (redirectResponse && redirectResponse.account) {
        msalInstance.setActiveAccount(redirectResponse.account);
      }
    } catch (_error) {
      // Intentionally suppress auth debug logging in production builds.
    }

    // Default to the first account if none active on page load.
    if (
      !msalInstance.getActiveAccount() &&
      msalInstance.getAllAccounts().length > 0
    ) {
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    } else if (!msalInstance.getActiveAccount()) {
      // Silent SSO for new tabs in the same browser. Backgrounded so its
      // hidden-iframe timeout never delays first paint (this was the culprit).
      try {
        await msalInstance.ssoSilent({ scopes: loginRequest.scopes });
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
        }
      } catch (_error) {
        // Intentionally suppress auth debug logging in production builds.
      }
    }
  })();
});
